import { useCallback, useEffect, useRef } from "react";
import { useCanvasStore } from "./store/canvas-store";
import { CanvasGrid } from "./canvas-grid";
import { CanvasObjectsLayer } from "./canvas-objects-layer";
import { CanvasPenLayer, type PenLayerHandle } from "./canvas-pen-layer";
import { CanvasCursorsLayer } from "./canvas-cursors-layer";
import { CanvasSelectionOverlay } from "./canvas-selection-overlay";
import {
  CANVAS_HEIGHT_PX,
  CANVAS_WIDTH_PX,
  type ViewState,
} from "../../lib/canvas-coordinates";
import { useKeyboardPan } from "./input/use-keyboard-pan";
import { useWheelPanZoom } from "./input/use-wheel-pan-zoom";
import { usePasteHandler } from "./input/use-paste-handler";
import { useDropHandler } from "./input/use-drop-handler";
import { usePointerMode } from "./input/use-pointer-mode";
import { useGlobalShortcuts } from "./input/use-global-shortcuts";
import {
  clearCanvasHistory,
  type CanvasObjectDraft,
  type CanvasOpDispatcher,
} from "./use-canvas-op-dispatcher";
import { useCursorBroadcaster } from "./use-cursor-broadcaster";
import { useCanvasApi, type CanvasApiClient } from "../../lib/canvas-api";
import type { StrokePoint } from "../../lib/pen-stroke";
import { loadCanvasView, saveCanvasView } from "../../lib/canvas-view-preference";
import { STICKY_COLOR_HEX } from "../../lib/sticky-preference";
import type { CanvasObject } from "@refinement-canvas/domain";

interface Props {
  readonly canvasId: string;
  readonly dispatcher: CanvasOpDispatcher;
}

const STICKY_DEFAULT_W = 200;
const STICKY_DEFAULT_H = 160;
const TEXT_DEFAULT_W = 240;
const TEXT_DEFAULT_H = 48;
const SHAPE_DEFAULT_W = 160;
const SHAPE_DEFAULT_H = 120;
const IMAGE_DEFAULT_MAX = 480;
const DEFAULT_VIEW: ViewState = { panX: 0, panY: 0, zoom: 1 };

// 8K サイズ（7680x4320）の DOM ステージ。
// CSS transform: translate3d(panX,panY,0) scale(zoom) で全オブジェクトを一括変形する。
// 子レイヤはステージ座標で配置し、ヒットテストは DOM のイベントバブリングで処理する。
export function CanvasStage({ canvasId, dispatcher }: Props): JSX.Element {
  const view = useCanvasStore((s) => s.view);
  const tool = useCanvasStore((s) => s.tool);
  const shapeKind = useCanvasStore((s) => s.shapeKind);
  const stickyColor = useCanvasStore((s) => s.stickyColor);
  const setView = useCanvasStore((s) => s.setView);
  const containerRef = useRef<HTMLDivElement>(null);
  const penLayerRef = useRef<PenLayerHandle | null>(null);

  const canvasApi = useCanvasApi();

  useEffect(() => {
    clearCanvasHistory();
    const saved = loadCanvasView(canvasId);
    setView(saved ?? DEFAULT_VIEW);
  }, [canvasId, setView]);

  useEffect(() => {
    saveCanvasView(canvasId, view);
  }, [canvasId, view]);

  // ペンストローク確定時: 描画範囲を AABB に丸めて pen.stroke.commit op を送信
  const onStrokeCommit = useCallback(
    (params: { points: readonly StrokePoint[]; color: string; thickness: number }) => {
      const aabb = computeAabb(params.points, params.thickness);
      dispatcher.commitPenStroke(params.points, params.color, params.thickness, aabb);
    },
    [dispatcher]
  );

  // 付箋 / 図形 / テキストの配置
  const onPlaceObject = useCallback(
    ({ stagePoint }: { stagePoint: { x: number; y: number } }) => {
      switch (tool) {
        case "place-sticky":
          dispatcher.createObject({
            type: "sticky",
            x: stagePoint.x - STICKY_DEFAULT_W / 2,
            y: stagePoint.y - STICKY_DEFAULT_H / 2,
            w: STICKY_DEFAULT_W,
            h: STICKY_DEFAULT_H,
            text: "",
            color: STICKY_COLOR_HEX[stickyColor],
          });
          return;
        case "place-text":
          dispatcher.createObject({
            type: "text",
            x: stagePoint.x,
            y: stagePoint.y,
            w: TEXT_DEFAULT_W,
            h: TEXT_DEFAULT_H,
            text: "テキスト",
            fontSize: 16,
          });
          return;
        case "place-shape":
          dispatcher.createObject({
            type: "shape",
            shapeKind,
            x: stagePoint.x - SHAPE_DEFAULT_W / 2,
            y: stagePoint.y - SHAPE_DEFAULT_H / 2,
            w: SHAPE_DEFAULT_W,
            h: SHAPE_DEFAULT_H,
            stroke: "#1a1a2e",
          });
          return;
        default:
          return;
      }
    },
    [dispatcher, shapeKind, stickyColor, tool]
  );

  // クリップボード画像: presigned PUT でアップロードしてから image オブジェクトを作成
  const onPasteImage = useCallback(
    (file: File, position: { x: number; y: number }) => {
      void uploadAndPlaceImage(canvasApi, canvasId, dispatcher, file, position);
    },
    [canvasApi, canvasId, dispatcher]
  );

  // クリップボード URL: OGP 取得してリンクカード化
  const onPasteUrl = useCallback(
    (url: string, position: { x: number; y: number }) => {
      void (async () => {
        try {
          const og = await canvasApi.fetchOgp(url);
          dispatcher.createObject({
            type: "link-card",
            x: position.x,
            y: position.y,
            w: 320,
            h: 200,
            url,
            title: og.title ?? url,
            description: og.description,
            imageUrl: og.image,
            siteName: og.siteName,
          });
        } catch {
          // OGP 取得失敗時は URL テキストとして配置
          dispatcher.createObject({
            type: "text",
            x: position.x,
            y: position.y,
            w: TEXT_DEFAULT_W,
            h: TEXT_DEFAULT_H,
            text: url,
            color: "#1e63d4",
          });
        }
      })();
    },
    [canvasApi, dispatcher]
  );

  const onDropImage = useCallback(
    (file: File, position: { x: number; y: number }) => {
      void uploadAndPlaceImage(canvasApi, canvasId, dispatcher, file, position);
    },
    [canvasApi, canvasId, dispatcher]
  );

  const onPasteCanvasObjects = useCallback(
    (objects: readonly CanvasObject[], position: { x: number; y: number }) => {
      pasteCanvasObjects(objects, position, dispatcher);
    },
    [dispatcher]
  );

  const onDropVideo = useCallback(
    (file: File, position: { x: number; y: number }) => {
      void uploadAndPlaceVideo(canvasApi, canvasId, dispatcher, file, position);
    },
    [canvasApi, canvasId, dispatcher]
  );

  // 入力ハンドラを束ねる
  useGlobalShortcuts(dispatcher);
  useKeyboardPan();
  useWheelPanZoom(containerRef);
  useDropHandler(containerRef, { onDropImage, onDropVideo });
  usePasteHandler({ onPasteCanvasObjects, onPasteImage, onPasteUrl });
  usePointerMode({ stageRef: containerRef, penLayerRef }, { onStrokeCommit, onPlaceObject });
  useCursorBroadcaster();

  return (
    <div
      ref={containerRef}
      data-testid="canvas-container"
      className={
        tool === "panning"
          ? "canvas-cursor-grabbing"
          : tool === "draw-pen"
            ? "canvas-cursor-crosshair"
            : tool === "place-sticky" || tool === "place-shape" || tool === "place-text"
              ? "canvas-cursor-crosshair"
              : "canvas-cursor-default"
      }
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        touchAction: "none",
      }}
    >
      <div
        data-testid="canvas-stage"
        style={{
          position: "absolute",
          width: `${CANVAS_WIDTH_PX}px`,
          height: `${CANVAS_HEIGHT_PX}px`,
          transformOrigin: "0 0",
          transform: `translate3d(${view.panX}px, ${view.panY}px, 0) scale(${view.zoom})`,
          willChange: "transform",
          background: "var(--color-surface)",
          boxShadow: "0 0 0 1px var(--color-border)",
          pointerEvents: "none",
        }}
      >
        <CanvasGrid />
        <CanvasObjectsLayer />
        <CanvasSelectionOverlay />
        <CanvasPenLayer handleRef={penLayerRef} />
        <CanvasCursorsLayer />
      </div>
    </div>
  );
}


function pasteCanvasObjects(
  objects: readonly CanvasObject[],
  position: { x: number; y: number },
  dispatcher: CanvasOpDispatcher
): void {
  if (objects.length === 0) return;
  // 貼り付け対象全体の外接矩形を求め、マウス位置へ中心合わせで複製する。
  const bounds = objects.reduce(
    (acc, object) => ({
      left: Math.min(acc.left, object.x),
      top: Math.min(acc.top, object.y),
      right: Math.max(acc.right, object.x + object.w),
      bottom: Math.max(acc.bottom, object.y + object.h),
    }),
    { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity }
  );
  const offsetX = position.x - (bounds.left + bounds.right) / 2;
  const offsetY = position.y - (bounds.top + bounds.bottom) / 2;
  const drafts: CanvasObjectDraft[] = [];
  for (const object of objects) {
    drafts.push({
      ...stripManagedObjectFields(object, { x: offsetX, y: offsetY }),
      x: object.x + offsetX,
      y: object.y + offsetY,
    });
  }
  const pastedIds = dispatcher.createObjects(drafts).map((object) => object.id);
  useCanvasStore.getState().setSelectedObjectIds(pastedIds);
}

function stripManagedObjectFields(
  object: CanvasObject,
  offset: { readonly x: number; readonly y: number }
): CanvasObjectDraft {
  const {
    id: _id,
    version: _version,
    createdAt: _createdAt,
    createdBy: _createdBy,
    updatedAt: _updatedAt,
    updatedBy: _updatedBy,
    zOrder: _zOrder,
    ...draft
  } = object;
  if (draft.type === "pen-stroke") {
    return {
      ...draft,
      points: draft.points.map((point) => ({
        ...point,
        x: point.x + offset.x,
        y: point.y + offset.y,
      })),
    } as CanvasObjectDraft;
  }
  return draft;
}

/**
 * ストローク座標群と太さから外接矩形を求める。pen-stroke オブジェクトの x,y,w,h に使う。
 */
function computeAabb(
  points: readonly { x: number; y: number }[],
  thickness: number
): { x: number; y: number; w: number; h: number } {
  if (points.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  const first = points[0];
  if (!first) return { x: 0, y: 0, w: 0, h: 0 };
  let minX = first.x;
  let minY = first.y;
  let maxX = first.x;
  let maxY = first.y;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  const pad = thickness;
  return {
    x: minX - pad,
    y: minY - pad,
    w: maxX - minX + pad * 2,
    h: maxY - minY + pad * 2,
  };
}

/**
 * 画像ファイルを presigned PUT で添付バケットへアップロードし、
 * 完了通知で得た公開 URL を attachment.update op で全員へ伝搬する。
 *
 * 1) 即時 UX のため URL.createObjectURL でローカル URL を src にした image オブジェクトを配置
 * 2) presigned PUT で S3 にバイナリ送信
 * 3) /uploads:complete を叩き、CloudFront 公開 URL を取得（未設定なら null）
 * 4) publicUrl が取れたら attachment.update op を発行して src を永続 URL に差し替える
 *    publicUrl が null の場合（dev/mock）は blob: URL のまま（自分のセッション中のみ表示）
 */
async function uploadAndPlaceImage(
  canvasApi: CanvasApiClient,
  canvasId: string,
  dispatcher: CanvasOpDispatcher,
  file: File,
  position: { x: number; y: number }
): Promise<void> {
  const { objectId, localUrl } = await placeImageWithLocalUrl(dispatcher, file, position);
  try {
    const presigned = await canvasApi.presignImageUpload({
      canvasId,
      objectId,
      contentType: file.type || "image/png",
      contentLength: file.size,
    });
    await fetch(presigned.uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "content-type": file.type || "image/png" },
    });
    const completed = await canvasApi.notifyUploadComplete(
      canvasId,
      presigned.bucket,
      presigned.key
    );
    if (completed.publicUrl) {
      dispatcher.updateAttachmentSrc(objectId, completed.publicUrl);
      URL.revokeObjectURL(localUrl);
    }
  } catch (e) {
    console.warn("[upload] 画像アップロードに失敗、ローカル URL のまま継続", e);
  }
}

async function uploadAndPlaceVideo(
  canvasApi: CanvasApiClient,
  canvasId: string,
  dispatcher: CanvasOpDispatcher,
  file: File,
  position: { x: number; y: number }
): Promise<void> {
  const { objectId, localUrl } = await placeVideoWithLocalUrl(dispatcher, file, position);
  try {
    const presigned = await canvasApi.presignVideoUpload({
      canvasId,
      objectId,
      contentType: file.type || "video/mp4",
      contentLength: file.size,
    });
    await fetch(presigned.uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "content-type": file.type || "video/mp4" },
    });
    const completed = await canvasApi.notifyUploadComplete(
      canvasId,
      presigned.bucket,
      presigned.key
    );
    if (completed.publicUrl) {
      dispatcher.updateAttachmentSrc(objectId, completed.publicUrl);
      URL.revokeObjectURL(localUrl);
    }
  } catch (e) {
    console.warn("[upload] 動画アップロードに失敗、ローカル URL のまま継続", e);
  }
}

async function placeImageWithLocalUrl(
  dispatcher: CanvasOpDispatcher,
  file: File,
  position: { x: number; y: number }
): Promise<{ objectId: string; localUrl: string }> {
  const localUrl = URL.createObjectURL(file);
  const dimensions = await readImageSize(localUrl);
  const placed = dispatcher.createObject({
    type: "image",
    x: position.x - dimensions.w / 2,
    y: position.y - dimensions.h / 2,
    w: dimensions.w,
    h: dimensions.h,
    src: localUrl,
    alt: file.name,
  });
  return { objectId: placed.id, localUrl };
}

async function placeVideoWithLocalUrl(
  dispatcher: CanvasOpDispatcher,
  file: File,
  position: { x: number; y: number }
): Promise<{ objectId: string; localUrl: string }> {
  const localUrl = URL.createObjectURL(file);
  const placed = dispatcher.createObject({
    type: "video",
    x: position.x - 240,
    y: position.y - 135,
    w: 480,
    h: 270,
    src: localUrl,
    mimeType: file.type || "video/mp4",
  });
  return { objectId: placed.id, localUrl };
}

function readImageSize(url: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(1, IMAGE_DEFAULT_MAX / Math.max(img.naturalWidth, img.naturalHeight));
      resolve({ w: img.naturalWidth * ratio, h: img.naturalHeight * ratio });
    };
    img.onerror = () => resolve({ w: 320, h: 240 });
    img.src = url;
  });
}
